import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { api } from '../services/api';
import { useCondominio } from '../contexts/CondominioContext';
import { BarChart3, Download, Calendar, FileText, Building2 } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const Relatorios = () => {
  const { getCondominioId, getCondominioNome } = useCondominio();
  const condominioId = getCondominioId();
  const [selectedReport, setSelectedReport] = useState('dashboard');
  const [dateRange, setDateRange] = useState({
    inicio: new Date().toISOString().split('T')[0],
    fim: new Date().toISOString().split('T')[0]
  });

  const { data: dashboardData, isLoading: dashboardLoading } = useQuery(
    ['dashboard-report', condominioId],
    () => api.get('/relatorios/dashboard').then(res => res.data.data)
  );

  const { data: visitantesData, isLoading: visitantesLoading } = useQuery(
    ['visitantes-report', condominioId, dateRange],
    () => api.get('/relatorios/visitantes', {
      params: {
        dataInicio: dateRange.inicio,
        dataFim: dateRange.fim
      }
    }).then(res => res.data.data),
    { enabled: selectedReport === 'visitantes' }
  );

  const { data: encomendasData, isLoading: encomendasLoading } = useQuery(
    ['encomendas-report', condominioId, dateRange],
    () => api.get('/relatorios/encomendas', {
      params: {
        dataInicio: dateRange.inicio,
        dataFim: dateRange.fim
      }
    }).then(res => res.data.data),
    { enabled: selectedReport === 'encomendas' }
  );

  const { data: ocorrenciasData, isLoading: ocorrenciasLoading } = useQuery(
    ['ocorrencias-report', condominioId, dateRange],
    () => api.get('/relatorios/ocorrencias', {
      params: {
        dataInicio: dateRange.inicio,
        dataFim: dateRange.fim
      }
    }).then(res => res.data.data),
    { enabled: selectedReport === 'ocorrencias' }
  );

  const { data: unidadesMoradoresData, isLoading: unidadesMoradoresLoading } = useQuery(
    ['unidades-moradores-report', condominioId],
    () => api.get('/relatorios/unidades-moradores').then(res => res.data.data),
    { enabled: selectedReport === 'unidades-moradores' && !!condominioId }
  );

  const reports = [
    { id: 'dashboard', name: 'Dashboard', icon: BarChart3 },
    { id: 'unidades-moradores', name: 'Unidades e Moradores', icon: Building2 },
    { id: 'visitantes', name: 'Visitantes', icon: FileText },
    { id: 'encomendas', name: 'Encomendas', icon: FileText },
    { id: 'ocorrencias', name: 'Ocorrências', icon: FileText }
  ];

  const isLoading = dashboardLoading || visitantesLoading || encomendasLoading || ocorrenciasLoading || unidadesMoradoresLoading;

  const formatUnidade = (unidade) => {
    if (!unidade) return '-';
    const bloco = unidade.apartamento_bloco ? `${unidade.apartamento_bloco}` : '';
    const numero = unidade.apartamento_numero || '';
    return [bloco, numero].filter(Boolean).join(' / ') || '-';
  };

  const formatTipoMorador = (tipo) => {
    const map = {
      proprietario: 'Proprietário',
      inquilino: 'Inquilino',
      morador_temporario: 'Temporário'
    };
    return map[tipo] || tipo || '-';
  };

  const gerarPdfUnidadesMoradores = () => {
    if (!unidadesMoradoresData?.linhas) {
      if (window.showNotification) {
        window.showNotification('Não há dados para gerar o PDF.', 'warning');
      }
      return;
    }

    const nomeCondominio = getCondominioNome() || 'Condomínio';
    const geradoEm = new Date().toLocaleString('pt-BR');
    const stats = unidadesMoradoresData.estatisticas || {};

    const linhasHtml = unidadesMoradoresData.linhas.map((linha) => {
      const moradores = linha.moradores || [];
      if (moradores.length === 0) {
        return `
          <tr>
            <td>${formatUnidade(linha.unidade)}</td>
            <td>${linha.unidade?.apartamento_andar ?? '-'}</td>
            <td>${linha.unidade?.apartamento_tipo || '-'}</td>
            <td colspan="4"><em>Sem moradores cadastrados</em></td>
          </tr>
        `;
      }
      return moradores.map((morador, index) => `
        <tr>
          ${index === 0 ? `
            <td rowspan="${moradores.length}">${formatUnidade(linha.unidade)}</td>
            <td rowspan="${moradores.length}">${linha.unidade?.apartamento_andar ?? '-'}</td>
            <td rowspan="${moradores.length}">${linha.unidade?.apartamento_tipo || '-'}</td>
          ` : ''}
          <td>${morador.morador_nome || '-'}</td>
          <td>${formatTipoMorador(morador.morador_tipo)}</td>
          <td>${morador.morador_telefone || '-'}</td>
          <td>${morador.morador_email || '-'}</td>
        </tr>
      `).join('');
    }).join('');

    const html = `
      <!DOCTYPE html>
      <html lang="pt-BR">
      <head>
        <meta charset="UTF-8" />
        <title>Relatório de Unidades e Moradores</title>
        <style>
          body { font-family: Arial, sans-serif; color: #111; padding: 24px; }
          h1 { font-size: 20px; margin: 0 0 4px; }
          p { margin: 0 0 8px; color: #444; }
          .stats { display: flex; gap: 16px; margin: 16px 0 20px; }
          .stat { border: 1px solid #ddd; padding: 8px 12px; border-radius: 6px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; vertical-align: top; }
          th { background: #f3f4f6; }
          @media print { button { display: none; } }
        </style>
      </head>
      <body>
        <h1>Relatório de Unidades e Moradores</h1>
        <p><strong>${nomeCondominio}</strong></p>
        <p>Gerado em ${geradoEm}</p>
        <div class="stats">
          <div class="stat">Unidades: <strong>${stats.totalUnidades || 0}</strong></div>
          <div class="stat">Unidades vazias: <strong>${stats.unidadesVazias || 0}</strong></div>
          <div class="stat">Moradores: <strong>${stats.totalMoradores || 0}</strong></div>
          <div class="stat">Moradores ativos: <strong>${stats.moradoresAtivos || 0}</strong></div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Unidade</th>
              <th>Andar</th>
              <th>Tipo</th>
              <th>Morador</th>
              <th>Vínculo</th>
              <th>Telefone</th>
              <th>E-mail</th>
            </tr>
          </thead>
          <tbody>
            ${linhasHtml || '<tr><td colspan="7">Nenhum registro encontrado</td></tr>'}
          </tbody>
        </table>
        <script>window.onload = function () { window.print(); }</script>
      </body>
      </html>
    `;

    const win = window.open('', '_blank');
    if (!win) {
      if (window.showNotification) {
        window.showNotification('Permita pop-ups para gerar o PDF.', 'error');
      }
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  const renderReport = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      );
    }

    switch (selectedReport) {
      case 'dashboard':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-indigo-600">
                    {dashboardData?.totais?.unidades || 0}
                  </div>
                  <div className="text-sm text-gray-500">Unidades</div>
                </div>
              </div>
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {dashboardData?.totais?.moradores || 0}
                  </div>
                  <div className="text-sm text-gray-500">Moradores</div>
                </div>
              </div>
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {dashboardData?.hoje?.visitantes || 0}
                  </div>
                  <div className="text-sm text-gray-500">Visitantes Hoje</div>
                </div>
              </div>
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-yellow-600">
                    {dashboardData?.hoje?.encomendasPendentes || 0}
                  </div>
                  <div className="text-sm text-gray-500">Encomendas Pendentes</div>
                </div>
              </div>
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {dashboardData?.hoje?.ocorrenciasAbertas || 0}
                  </div>
                  <div className="text-sm text-gray-500">Ocorrências Abertas</div>
                </div>
              </div>
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-emerald-600">
                    {dashboardData?.hoje?.usuariosAtivos || 0}
                  </div>
                  <div className="text-sm text-gray-500">Usuários Ativos</div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'unidades-moradores':
        if (!condominioId) {
          return <p className="text-sm text-gray-500">Selecione um condomínio para visualizar o relatório.</p>;
        }
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-4">
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-gray-900">{unidadesMoradoresData?.estatisticas?.totalUnidades || 0}</div>
                  <div className="text-sm text-gray-500">Unidades</div>
                </div>
              </div>
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-yellow-600">{unidadesMoradoresData?.estatisticas?.unidadesVazias || 0}</div>
                  <div className="text-sm text-gray-500">Unidades vazias</div>
                </div>
              </div>
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-green-600">{unidadesMoradoresData?.estatisticas?.totalMoradores || 0}</div>
                  <div className="text-sm text-gray-500">Moradores</div>
                </div>
              </div>
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-blue-600">{unidadesMoradoresData?.estatisticas?.moradoresAtivos || 0}</div>
                  <div className="text-sm text-gray-500">Moradores ativos</div>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unidade</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Andar</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Moradores</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(unidadesMoradoresData?.linhas || []).map((linha) => (
                    <tr key={linha.unidade?.apartamento_id}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{formatUnidade(linha.unidade)}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{linha.unidade?.apartamento_andar ?? '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{linha.unidade?.apartamento_tipo || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {(linha.moradores || []).length === 0 ? (
                          <span className="text-gray-400">Sem moradores</span>
                        ) : (
                          <ul className="space-y-1">
                            {linha.moradores.map((morador) => (
                              <li key={morador.morador_id}>
                                <span className="font-medium">{morador.morador_nome}</span>
                                {' · '}
                                {formatTipoMorador(morador.morador_tipo)}
                                {morador.morador_telefone ? ` · ${morador.morador_telefone}` : ''}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(unidadesMoradoresData?.linhas || []).length === 0 && (
                <p className="text-sm text-gray-500 py-6 text-center">Nenhuma unidade cadastrada neste condomínio.</p>
              )}
            </div>
          </div>
        );

      case 'visitantes':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-4">
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-gray-900">{visitantesData?.estatisticas?.total || 0}</div>
                  <div className="text-sm text-gray-500">Total</div>
                </div>
              </div>
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-green-600">{visitantesData?.estatisticas?.finalizados || 0}</div>
                  <div className="text-sm text-gray-500">Finalizados</div>
                </div>
              </div>
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-red-600">{visitantesData?.estatisticas?.negados || 0}</div>
                  <div className="text-sm text-gray-500">Negados</div>
                </div>
              </div>
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-blue-600">{visitantesData?.estatisticas?.autorizados || 0}</div>
                  <div className="text-sm text-gray-500">Autorizados</div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'encomendas':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-4">
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-gray-900">{encomendasData?.estatisticas?.total || 0}</div>
                  <div className="text-sm text-gray-500">Total</div>
                </div>
              </div>
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-green-600">{encomendasData?.estatisticas?.entregues || 0}</div>
                  <div className="text-sm text-gray-500">Entregues</div>
                </div>
              </div>
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-yellow-600">{encomendasData?.estatisticas?.aguardando || 0}</div>
                  <div className="text-sm text-gray-500">Aguardando</div>
                </div>
              </div>
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-red-600">{encomendasData?.estatisticas?.devolvidas || 0}</div>
                  <div className="text-sm text-gray-500">Devolvidas</div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'ocorrencias':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-4">
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-gray-900">{ocorrenciasData?.estatisticas?.total || 0}</div>
                  <div className="text-sm text-gray-500">Total</div>
                </div>
              </div>
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-red-600">{ocorrenciasData?.estatisticas?.abertas || 0}</div>
                  <div className="text-sm text-gray-500">Abertas</div>
                </div>
              </div>
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-yellow-600">{ocorrenciasData?.estatisticas?.emAndamento || 0}</div>
                  <div className="text-sm text-gray-500">Em Andamento</div>
                </div>
              </div>
              <div className="card">
                <div className="card-body text-center">
                  <div className="text-2xl font-bold text-green-600">{ocorrenciasData?.estatisticas?.resolvidas || 0}</div>
                  <div className="text-sm text-gray-500">Resolvidas</div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return <div>Selecione um relatório</div>;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Relatórios</h1>
        <p className="mt-1 text-sm text-gray-500">
          Visualize relatórios e estatísticas do condomínio
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Relatórios</h3>
            </div>
            <div className="card-body p-0">
              <nav className="space-y-1">
                {reports.map((report) => {
                  const Icon = report.icon;
                  return (
                    <button
                      key={report.id}
                      onClick={() => setSelectedReport(report.id)}
                      className={`w-full flex items-center px-4 py-2 text-sm font-medium text-left transition-colors ${
                        selectedReport === report.id
                          ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <Icon className="mr-3 h-5 w-5" />
                      {report.name}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="card">
            <div className="card-header">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">
                  {reports.find(r => r.id === selectedReport)?.name}
                </h3>
                {selectedReport === 'unidades-moradores' && (
                  <button className="btn-secondary" onClick={gerarPdfUnidadesMoradores}>
                    <Download className="h-4 w-4 mr-2" />
                    Gerar PDF
                  </button>
                )}
                {selectedReport !== 'dashboard' && selectedReport !== 'unidades-moradores' && (
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <input
                        type="date"
                        value={dateRange.inicio}
                        onChange={(e) => setDateRange({ ...dateRange, inicio: e.target.value })}
                        className="input text-sm"
                      />
                      <span className="text-gray-500">até</span>
                      <input
                        type="date"
                        value={dateRange.fim}
                        onChange={(e) => setDateRange({ ...dateRange, fim: e.target.value })}
                        className="input text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="card-body">
              {renderReport()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Relatorios;
